// Self-test: send a coded text to your own phone, reply with the code, watch each step light up.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { formatPhone, normalizeE164, type SelfTestStepName } from '@bt/core';
import { api, type MeResponse, type SelfTest } from '../../api';
import { InlineError, Loading } from '../../components/States';
import { fmtFull, fmtTime } from '../../lib/format';

const STEP_WORDS: Record<SelfTestStepName, string> = {
  webhook_config_checked: 'Webhook settings checked at the provider',
  api_accepted: 'Provider accepted the text',
  phone_sent: 'Phone sent it',
  delivered: 'Carrier says delivered',
  inbound_webhook_seen: 'Your reply reached this app',
  reply_matched: 'Reply had the code',
};
const STEP_ORDER: SelfTestStepName[] = ['webhook_config_checked', 'api_accepted', 'phone_sent', 'delivered', 'inbound_webhook_seen', 'reply_matched'];

export function SelfTestPage({ me }: { me: MeResponse }) {
  const activeLines = me.lines.filter((l) => l.active !== false);
  const [lineId, setLineId] = useState(activeLines[0]?.id ?? '');
  const [cell, setCell] = useState(() => {
    try {
      return localStorage.getItem('bt:selftest:cell') ?? '';
    } catch {
      return '';
    }
  });
  const [currentId, setCurrentId] = useState<string | null>(null);
  const e164 = normalizeE164(cell);
  const tz = me.workspace.timezone;

  const history = useQuery({ queryKey: ['self-test', 'list', lineId], queryFn: () => api.selfTest.list(lineId), enabled: !!lineId });
  useEffect(() => {
    // show the running one (or the latest) when the page opens
    if (!currentId && history.data?.items.length) {
      const running = history.data.items.find((t) => t.status === 'running');
      setCurrentId((running ?? history.data.items[0]).id);
    }
  }, [history.data, currentId]);

  const current = useQuery({
    queryKey: ['self-test', currentId],
    queryFn: () => api.selfTest.get(currentId!),
    enabled: !!currentId,
    refetchInterval: (q) => (q.state.data?.self_test.status === 'running' ? 3000 : false),
  });

  const start = useMutation({
    mutationFn: () => api.selfTest.start(lineId, e164!),
    onSuccess: (r) => {
      setCurrentId(r.self_test.id);
      try {
        localStorage.setItem('bt:selftest:cell', cell);
      } catch {
        /* ignore */
      }
      history.refetch();
    },
  });

  const test = current.data?.self_test ?? null;

  return (
    <div className="page narrow">
      <div className="small">
        <Link to="/health">Health</Link> /
      </div>
      <h1>Self-test</h1>
      <p className="muted">Sends a coded text to your own cell. Reply with the code. Every step is checked, both directions. Takes about a minute; gives up after 10.</p>

      <div className="card">
        {activeLines.length > 1 && (
          <label className="field">
            <span>Line</span>
            <select className="input" value={lineId} onChange={(e) => setLineId(e.target.value)}>
              {activeLines.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label ? `${l.label} · ` : ''}
                  {formatPhone(l.phone_number)}
                </option>
              ))}
            </select>
          </label>
        )}
        {activeLines.length === 0 && <div className="notice warn">No texting line yet. Add one in Settings › Lines.</div>}
        <label className="field">
          <span>Your cell</span>
          <input className="input" value={cell} onChange={(e) => setCell(e.target.value)} placeholder="(239) 555-0123" />
          {cell && !e164 && <div className="tiny danger-text">Not a full US number yet.</div>}
        </label>
        <InlineError error={start.error} />
        <button className="btn primary" disabled={!e164 || !lineId || start.isPending || test?.status === 'running'} onClick={() => start.mutate()}>
          {start.isPending ? 'Starting' : test?.status === 'running' ? 'Running' : 'Start'}
        </button>
      </div>

      {currentId && current.isPending && <Loading label="Loading test" />}
      {test && <TestView test={test} tz={tz} />}

      <div className="card">
        <h2>History</h2>
        {history.isPending && <Loading />}
        {history.data && history.data.items.length === 0 && <div className="muted">No tests yet on this line.</div>}
        {history.data && history.data.items.length > 0 && (
          <ul className="list-plain">
            {history.data.items.map((t) => (
              <li key={t.id} className="row between">
                <button className="btn link" onClick={() => setCurrentId(t.id)}>
                  {fmtFull(t.started_at, tz)} · to {formatPhone(t.to_number)}
                </button>
                <span className={`chip ${t.status === 'passed' ? 'ok' : t.status === 'failed' ? 'danger' : 'warn'}`}>{t.status.toUpperCase()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TestView({ test, tz }: { test: SelfTest; tz: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (test.status !== 'running') return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [test.status]);
  const left = Math.max(0, Math.round((new Date(test.timeout_at).getTime() - now) / 1000));
  const steps = STEP_ORDER.map((name) => test.steps.find((s) => s.name === name) ?? { name, ok: null, at: null, detail: null });

  return (
    <div className="card">
      <div className="row between wrap">
        <div>
          <strong>Code {test.code}</strong> <span className="muted small">· to {formatPhone(test.to_number)} · started {fmtTime(test.started_at, tz)}</span>
        </div>
        {test.status === 'running' ? (
          <span className="chip warn">
            running · {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')} left
          </span>
        ) : (
          <span className={`chip ${test.status === 'passed' ? 'ok' : 'danger'}`}>{test.status === 'passed' ? 'PASS' : 'FAIL'}</span>
        )}
      </div>
      {test.status === 'running' && <p className="muted small" style={{ marginTop: 6 }}>When the text arrives on your cell, reply with just the code: {test.code}</p>}
      <ul className="steps" style={{ marginTop: 10 }}>
        {steps.map((s) => (
          <li key={s.name}>
            <span className={`mark ${s.ok === true ? 'ok' : s.ok === false ? 'fail' : s.at ? 'skip' : ''}`}>{s.ok === true ? '✓' : s.ok === false ? '!' : s.at ? '–' : ''}</span>
            <span>
              {STEP_WORDS[s.name as SelfTestStepName] ?? s.name}
              {s.detail && <div className="tiny muted">{s.detail}</div>}
            </span>
            <span className="muted small nowrap">{s.at ? fmtTime(s.at, tz) : ''}</span>
          </li>
        ))}
      </ul>
      {test.status !== 'running' && (
        <div className={`notice ${test.status === 'passed' ? 'ok' : 'danger'}`} style={{ marginTop: 12 }}>
          {test.status === 'passed' ? 'Both directions work. The line is ready.' : test.diagnosis ?? 'The test did not finish.'}
        </div>
      )}
    </div>
  );
}
